import {
  Badge,
  Kpi,
  Module,
  ModuleBody,
  ModuleHead,
} from "@vjal/ui";
import { DataTableCollection } from "@vjal/ui";
import { CalendarClock, Download, ShieldCheck, Users } from "lucide-react";
import type { ReactNode } from "react";
import { CapabilityButton } from "../components/AccessState";
import { evaluateActionCapability, type RouteAccessDecision } from "../domain/capabilities";
import type { CoachingRole } from "../domain/roles";
import { useCoachingDashboardFoundation } from "../hooks/useCoachingQueries";
import { isLiveCoachingDataMode } from "../hooks/useLiveProductData";
import {
  cohorts,
  companies,
  reports,
  sessions,
  type ProductScreenContract,
} from "./product-route-contracts";

/**
 * Non-focused operational shell for ProductRoutePage.
 * Isolated so ProductRoutePage is not a collection-source candidate: focused
 * surfaces render only customBody, and the inventory AST must not treat every
 * ProductRoutePage route mapping as a DataTableCollection owner.
 */
export function ProductRouteOperationalShell({
  decision,
  role,
  screen,
}: {
  decision: RouteAccessDecision;
  role: CoachingRole;
  screen: ProductScreenContract;
}) {
  const live = isLiveCoachingDataMode();
  const foundation = useCoachingDashboardFoundation({ enabled: true, role });
  const kpiValue = (length: number | undefined, fixtureLength: number) =>
    length ?? (live ? "—" : fixtureLength);

  return (
    <>
      <Module span={3}>
        <Kpi
          label="Companies"
          value={kpiValue(foundation.companies.data?.items.length, companies.length)}
          meta={role === "company_admin" ? "exact assigned company" : "visible scope"}
        />
      </Module>
      <Module span={3}>
        <Kpi label="Cohorts" value={kpiValue(foundation.cohorts.data?.items.length, cohorts.length)} meta="current records" />
      </Module>
      <Module span={3}>
        <Kpi label="Sessions" value={kpiValue(foundation.sessions.data?.items.length, sessions.length)} meta="schedule scope" />
      </Module>
      <Module span={3}>
        <Kpi label="Reports" value={kpiValue(foundation.reports.data?.items.length, reports.length)} meta="artifact scope" />
      </Module>

      <Module span={8}>
        <ModuleHead title={screen.workTitle} subtitle={screen.secondaryJob} />
        <ModuleBody flush>
          <DataTableCollection
            caption={screen.workTitle}
            columns={screen.columns}
            data={screen.rows}
            pageSize={screen.rows.length > 6 ? 6 : 0}
            searchPlaceholder={`Filter ${screen.workTitle.toLowerCase()}`}
            stableChipNames
          />
        </ModuleBody>
      </Module>

      <Module span={4}>
        <ModuleHead title="Operations" subtitle="Available actions and scoped controls." />
        <ModuleBody className="space-y-4">
          <ActionRow icon={<Users size={18} />} label="Bulk import participants">
            <CapabilityButton decision={evaluateActionCapability(role, "participants:bulk-import")}>
              Bulk import
            </CapabilityButton>
          </ActionRow>
          <ActionRow icon={<CalendarClock size={18} />} label="Create 10-session series">
            <CapabilityButton decision={evaluateActionCapability(role, "sessions:create-series")}>
              Create series
            </CapabilityButton>
          </ActionRow>
          <ActionRow icon={<Download size={18} />} label="CSV export">
            <CapabilityButton decision={evaluateActionCapability(role, "exports:csv")}>Export CSV</CapabilityButton>
          </ActionRow>
          <ActionRow icon={<ShieldCheck size={18} />} label="Visible scope">
            <Badge variant={decision.recordScopeStatus === "unknown" ? "warning" : "success"}>
              {decision.recordScopeStatus ?? "in-scope"}
            </Badge>
          </ActionRow>
        </ModuleBody>
      </Module>
    </>
  );
}

function ActionRow({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border border-line px-3 py-2">
      <div className="flex items-center gap-2 text-sm text-ink-secondary">
        <span className="text-ink-muted">{icon}</span>
        <span>{label}</span>
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}
